import { useMemo } from 'react';
import {
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiEntityType,
} from '../../../api-internal';
import { useQueryEntitySchema } from '../../../schema/entitySchemaActions';
import Tracker from '../../../tracking/tracker';
import { isVisible } from '../../../schema/helper';

export const useUsableFieldsBuilder = (selectedFieldIds: string[], entityType: InternalApiEntityType) => {
  const { data } = useQueryEntitySchema(entityType);
  const allFields = useMemo(() => {
    return (data?.schemaFields || [])
      .filter((field) => isVisible(field))
      .filter(
        (sf) =>
          sf.type !== InternalApiEntitySchemaFieldType.DATETIME && sf.type !== InternalApiEntitySchemaFieldType.ENUM
      )
      .sort(sortFields)
      .reduce((acc, field) => {
        if (!field.parentId) {
          acc.set(field.id, convertSchemaFieldToScreenableFieldPart(field));
          return acc;
        } else {
          const parent = acc.get(field.parentId);
          if (!parent) {
            console.error(
              `Invalid schema configuration: field "${field.fieldName}" (${field.id}) references a parent (${field.parentId}) that is hidden or unavailable; the field will be omitted from screening setup.`
            );
          }
          parent?.children.push(convertSchemaFieldToScreenableFieldPart(field, parent));
          return acc;
        }
      }, new Map<string, ScreenableFieldPart>());
  }, [data]);

  const selectedFields = useMemo(() => {
    const flatMappedFieldsById = Array.from(allFields.values())
      .flatMap((sf) => [sf, ...sf.children])
      .reduce((acc, field) => {
        acc.set(field.id, field);
        return acc;
      }, new Map<string, ScreenableFieldPart>());
    return selectedFieldIds
      .map((id) => {
        const selectedField = flatMappedFieldsById.get(id);
        if (!selectedField) {
          Tracker.error(new Error('Selected field not found in schema'));
        }
        return selectedField;
      })
      .filter((sf) => sf) as ScreenableFieldPart[];
  }, [allFields, selectedFieldIds]);

  const [selectableFieldIds, errors] = useMemo(() => {
    const errors: string[] = [];
    let ids = Array.from(allFields.values())
      .flatMap((sf) => [sf, ...sf.children])
      .filter((field) => field.children.length <= 0);
    // object check
    const selectedParentIds = new Set(selectedFields.map((sf) => sf.parentId));
    if (selectedParentIds.size > 1) {
      errors.push('Only parameters from one object can be selected together');
    } else if (selectedParentIds.size === 1) {
      const parentId = Array.from(selectedParentIds)[0];
      ids = ids.filter((sf) => sf.parentId === parentId);
    }
    // array check
    const arraysSelected = selectedFields.filter((sf) => sf.isArray).length;
    const notOnlyArraysSelected = arraysSelected > 0 && arraysSelected < selectedFields.length;
    if (notOnlyArraysSelected) {
      errors.push('Arrays and regular fields cannot be selected together');
    } else if (arraysSelected > 1) {
      errors.push('Only one array can be selected');
    } else if (arraysSelected === 1) {
      ids = selectedFields;
    } else if (arraysSelected === 0 && selectedFields.length > 0) {
      ids = ids.filter((sf) => !sf.isArray);
    }
    // return
    if (errors.length > 0) {
      return [new Set(selectedFields.map((sf) => sf.id)), errors];
    } else {
      return [new Set(ids.map((sf) => sf.id)), []];
    }
  }, [allFields, selectedFields]);

  return [allFields, selectedFields, selectableFieldIds, errors] as const;
};

export type ScreenableFieldPart = {
  id: string;
  parentId: string | null;
  displayName: string;
  fullFieldName?: string;
  fieldName: string;
  isArray: boolean;
  type: InternalApiEntitySchemaFieldType;
  sortOrder: number;
  children: ScreenableFieldPart[];
};

const convertSchemaFieldToScreenableFieldPart = (
  field: InternalApiEntitySchemaField,
  parentField: ScreenableFieldPart | undefined = undefined
): ScreenableFieldPart => {
  let fullFieldName;
  if (parentField) {
    const parentFieldName = parentField?.isArray ? parentField?.fieldName + '[].' : parentField?.fieldName + '.';
    fullFieldName = parentFieldName + field.fieldName;
  } else {
    fullFieldName = field.fieldName;
  }
  return {
    id: field.id,
    parentId: field.parentId || null,
    displayName: field.displayName,
    fullFieldName: fullFieldName,
    fieldName: field.fieldName,
    isArray: field.isMulti,
    type: field.type,
    sortOrder: field.sortOrder,
    children: [],
  };
};

const sortFields = (a: InternalApiEntitySchemaField, b: InternalApiEntitySchemaField) => {
  // parentId nulls first, then sort by sortOrder
  if (!a.parentId && b.parentId) {
    return -1;
  } else if (a.parentId && !b.parentId) {
    return 1;
  } else {
    return a.sortOrder - b.sortOrder;
  }
};
